import { audioContextOrNull } from '../browser.js';
import type { LiveSocketLike, LiveTranscriptSeams } from './live-transcript.js';

/**
 * The real browser pieces behind the realtime session, kept to pass-throughs so every behaviour
 * lives in the tested controller. Stopping the pump releases the microphone, the node and the
 * context.
 *
 * The socket is cookie-authenticated by the browser itself; no credential appears in the URL.
 *
 * **The worklet is a file on the site, not a blob.** It was an inline module compiled to a
 * `blob:` URL, and a worklet module is fetched under the page's script rule — which admits the
 * origin and not `blob:`. On the deployed site the module was blocked, `addModule` threw, and
 * every hold-to-talk ended with "the live transcription failed" (founder, 2026-09-13). Chromium
 * raises no violation event for a worklet fetch, so nothing in the console said why. Measured
 * both ways against the running host before it was changed.
 */
const WORKLET_URL = '/pcm-capture.worklet.js';

export function browserLiveSeams(baseUrl: string): LiveTranscriptSeams {
  return {
    openSocket: (sessionId): LiveSocketLike => {
      // Same-origin deployments pass an empty base; the socket then derives from the page itself.
      const httpBase = baseUrl.length > 0 ? baseUrl : globalThis.location.origin;
      return new WebSocket(
        `${httpBase.replace(/^http/u, 'ws')}/v1/sessions/${sessionId}/voice-live`,
      );
    },
    openPump: async (onBlock) => {
      if (audioContextOrNull === null) throw new Error('web_audio_unavailable');
      const stream = await globalThis.navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
      });
      const context = new audioContextOrNull();
      // From the page's own origin, never the API base: the module is a static asset of the site.
      await context.audioWorklet.addModule(WORKLET_URL);
      const source = context.createMediaStreamSource(stream);
      const node = new AudioWorkletNode(context, 'bilal-pcm-capture');
      node.port.onmessage = (event: MessageEvent): void => {
        if (event.data instanceof Float32Array) onBlock(event.data, context.sampleRate);
      };
      // Capture only: the graph never reaches the destination, so the person never hears themselves.
      source.connect(node);
      return {
        stop: (): void => {
          node.port.onmessage = null;
          source.disconnect();
          node.disconnect();
          for (const track of stream.getTracks()) track.stop();
          void context.close().catch(() => undefined);
        },
      };
    },
    setTimer: (handler, ms) => setTimeout(handler, ms),
    clearTimer: (timer) => {
      clearTimeout(timer);
    },
  };
}
